import React from "react";

import Spacer from "shared/components/Spacer";

import {
  PERSONA_CHECK_STATUS,
  PERSONA_VERIFICATION_TYPES,
} from "shared/config/constants";
import { VerificationCheck } from "shared/utils/interfaces";

import FlexContainer from "../FlexContainer";
import Icon from "../Icon";
import { RegularText, SmallText } from "../Typography";
import Well from "../Well";

import { ReasonListWrapper } from "./styled";

import { mapReasonsByCheck, applyDisplayOverrides } from "./utils";

export interface VerificationErrorMessagesProps {
  checks: {
    requirement: null | "required" | "not_required";
    name: string;
    reasons: string[];
    status: NonNullable<"passed" | "failed" | "not_applicable">;
  }[];
  verificationType?: string;
}

const VerificationErrorMessages = ({
  checks,
  verificationType,
}: VerificationErrorMessagesProps) => {
  // Apply display overrides before filtering checks by requirement
  const processedChecks = applyDisplayOverrides(checks as VerificationCheck[]);

  const failedChecks = processedChecks
    .filter((check) => check.status === PERSONA_CHECK_STATUS.FAILED)
    .filter(
      (check) => check.requirement === "required" || check.requirement === null
    );
  const warningChecks = processedChecks
    .filter((check) => check.status === PERSONA_CHECK_STATUS.FAILED)
    .filter((check) => check.requirement === "not_required");
  const failureReasonsChecksMap = mapReasonsByCheck(
    failedChecks as VerificationCheck[],
    verificationType || PERSONA_VERIFICATION_TYPES.GOVERNMENT_ID
  );
  const potentialFraudRisksChecksMap = mapReasonsByCheck(
    warningChecks as VerificationCheck[],
    verificationType || PERSONA_VERIFICATION_TYPES.GOVERNMENT_ID
  );

  const renderFailureReasons = () => {
    return (
      <Well error>
        <SmallText strong>Verification Issues Detected</SmallText>
        <ReasonListWrapper>
          {Array.from(failureReasonsChecksMap.entries()).map(
            ([checkName, reasons]) => (
              <FlexContainer
                key={checkName}
                alignItems="center"
                className="reason-row"
              >
                <Icon.OrangeExclamationSmall className="info-icon" />
                <RegularText>
                  <strong>{checkName}</strong> — {reasons.join(", ")}
                </RegularText>
              </FlexContainer>
            )
          )}
        </ReasonListWrapper>
      </Well>
    );
  };

  const renderPotentialFraudRisks = () => {
    return (
      <>
        {failureReasonsChecksMap.size > 0 && <Spacer size="sm" />}
        <Well style={{ backgroundColor: "#FFF4D6" }}>
          <SmallText strong>Non-Critical Verification Flags</SmallText>
          <Spacer size="xs" />
          <SmallText style={{ fontStyle: "italic" }}>
            These checks did not pass but do not block ID verification.
          </SmallText>
          <ReasonListWrapper>
            {Array.from(potentialFraudRisksChecksMap.entries()).map(
              ([checkName, reasons]) => (
                <FlexContainer
                  key={checkName}
                  alignItems="center"
                  className="reason-row"
                >
                  <Icon.OrangeExclamationSmall className="info-icon" />
                  <RegularText>
                    <strong>{checkName}</strong> — {reasons.join(", ")}
                  </RegularText>
                </FlexContainer>
              )
            )}
          </ReasonListWrapper>
        </Well>
      </>
    );
  };

  return (
    <>
      {failureReasonsChecksMap.size > 0 && renderFailureReasons()}
      {potentialFraudRisksChecksMap.size > 0 && renderPotentialFraudRisks()}
    </>
  );
};

export default VerificationErrorMessages;
